import { forwardRef } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import {
  hexToHsl,
  WHITE,
  BLACK,
  PG20S,
  PG90T,
  PG10S,
  SLB,
  SLB90T,
  SLB10S,
  SLR,
  SLR90T,
  SLR10S,
  SO,
  SO90T,
  SO10S,
  SY,
  SY90T,
  SY10S,
  PB90T,
  PB80T,
  PB70T,
  PB20T
} from '../../colors'
import { base, vagRundFont } from '../../mixins'
import { Spinner } from '../../loading/spinner'

const loaderClassName = 'h2-btn-loader'
const paddings = {
  small: '5px 8px',
  medium: '6px 10px',
  default: '8px 16px',
  large: '10px 18px',
  variant: {
    link: {
      small: 0,
      medium: 0,
      large: 0,
      default: 0
    }
  }
}
const iconSizes = {
  small: 'tiny',
  medium: 'small',
  default: 'small',
  large: 'small'
}
const colors = {
  default: PG10S,
  blue: SLB,
  red: SLR,
  orange: SO,
  promo: SY,
  monochrome: BLACK
}
const hoverBgColors = {
  default: PG20S,
  monochrome: PB20T,
  promo: SY10S,
  blue: SLB10S,
  orange: SO10S,
  red: SLR10S,
  variant: {
    text: {
      default: PG90T,
      monochrome: PB90T,
      promo: SY90T,
      blue: SLB90T,
      orange: SO90T,
      red: SLR90T
    },
    outlined: colors,
    'outlined-cta': colors
  }
}
const fontSizes = {
  small: '12px',
  medium: '14px',
  default: '16px',
  large: '20px'
}
const fontColors = {
  default: WHITE,
  blue: WHITE,
  orange: WHITE,
  red: WHITE,
  monochrome: WHITE,
  promo: BLACK,
  variant: {
    link: colors,
    text: colors,
    outlined: {
      ...colors,
      promo: BLACK
    },
    'outlined-cta': {
      ...colors,
      promo: BLACK
    }
  }
}
const getFontColor = ({ display, variant, color, icon }) => {
  if (color) {
    return color
  }

  if (icon && /outline/.test(variant)) {
    return BLACK
  }

  return fontColors.variant[variant]?.[display] ?? fontColors[display]
}
const getHoverFontColor = ({ display, variant, color, icon, disabled }) => {
  if (disabled) {
    return PB70T
  }

  if (color) {
    return color
  }

  if (icon && /outline/.test(variant)) {
    return BLACK
  }

  if (/link|text/.test(variant)) {
    return colors[display]
  }

  return fontColors[display]
}
const getBackgroundColor = ({ display, variant, fill, disabled }) => {
  if (/link|text/.test(variant)) {
    return 'transparent'
  }

  if (disabled) {
    return /outline/.test(variant) ? WHITE : PB90T
  }

  if (fill) {
    return fill
  }

  if (/outline/.test(variant)) {
    return WHITE
  }

  return colors[display]
}
const getHoverBackgroundColor = ({ display, variant, disabled, icon, fill }) => {
  if (variant === 'link') {
    return 'transparent'
  }

  if (disabled) {
    return /outline|text/.test(variant) ? 'transparent' : PB90T
  }

  if (fill) {
    const [hue, sat, light] = hexToHsl(fill)

    // Add 10% shade by reducing the light value
    return `hsl(${hue}, ${sat}%, ${light - light / 10}%)`
  }

  if (icon && /outline/.test(variant)) {
    return '#f7f7f7'
  }

  return hoverBgColors.variant[variant]?.[display] ?? hoverBgColors[display]
}
const getBorderColor = ({ display, variant, icon, disabled }) => {
  if (/outline/.test(variant)) {
    if (disabled) {
      return PB90T
    }

    if (icon) {
      return PB90T
    }

    return colors[display]
  }

  return 'transparent'
}
const getImageMargin = ({ iconAtTop, align }) => {
  if (iconAtTop) {
    return 0
  }

  if (/right/.test(align)) {
    return '0 8px 0 0'
  }

  return '0 0 0 8px'
}
const getPadding = ({ variant, size }) => {
  return paddings.variant[variant]?.[size] ?? paddings[size]
}
const getFlexDirection = ({ iconAtTop, align }) => {
  if (iconAtTop) {
    return 'column'
  }

  if (/right/.test(align)) {
    return 'row-reverse'
  }

  return 'row'
}
const IconWrap = styled.span`
  display: inline-flex;
  margin: ${getImageMargin};
  order: ${({ iconAtTop }) => (iconAtTop ? 1 : 2)};

  > span[role='img'] {
    cursor: pointer;
  }
`
const TextWrap = styled.span`
  order: ${({ iconAtTop }) => (iconAtTop ? 2 : 1)};
`
const styles = css`
  ${base}
  ${({ variant }) => (/cta/.test(variant) ? vagRundFont : 'font-weight: 700')};
  color: ${getFontColor};
  height: ${({ isFullHeight }) => (isFullHeight ? '100%' : 'auto')};
  width: ${({ isFullWidth }) => (isFullWidth ? '100%' : 'auto')};
  font-size: ${({ size }) => fontSizes[size]};
  display: inline-flex;
  text-align: ${({ align }) => align};
  flex-direction: ${getFlexDirection};
  align-items: center;
  justify-content: ${({ align }) => (/center/.test(align) ? 'center' : 'space-between')};
  white-space: nowrap;
  background-color: ${getBackgroundColor};
  border-width: ${({ variant }) => (/outline/.test(variant) ? '1px' : '0')};
  border-color: ${getBorderColor};
  border-style: solid;
  border-radius: ${({ variant }) => (/link/.test(variant) ? 0 : '4px')};
  padding: ${getPadding};
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 5px auto blue;
    outline-offset: -2px;
  }

  &:hover {
    color: ${getHoverFontColor};
    background-color: ${getHoverBackgroundColor};

    > .${loaderClassName} {
      border-color: ${getHoverFontColor};
      border-bottom-color: ${getHoverBackgroundColor};
    }
  }

  &:disabled {
    color: ${PB80T};
    cursor: default;
  }

  > .${loaderClassName} {
    margin: ${getImageMargin};
    order: 3;
  }
`
const StyledButton = styled.button`
  ${styles};
`
const renderBody = ({
  display,
  size,
  variant,
  align,
  isLoading,
  isFullWidth,
  children,
  iconAtTop,
  icon: Icon
}) => {
  const color = getFontColor({ display, variant })

  return (
    <>
      <TextWrap isFullWidth={isFullWidth} iconAtTop={iconAtTop}>
        {children}
      </TextWrap>
      {Icon ? (
        <IconWrap align={align} iconAtTop={iconAtTop}>
          <Icon size={iconSizes[size]} />
        </IconWrap>
      ) : null}
      {isLoading ? (
        <Spinner size="small" color={color} className={loaderClassName} />
      ) : null}
    </>
  )
}
/**
 * The indispensable Button component.
 */
const Button = forwardRef(function Button(
  {
    children,
    variant,
    type = 'button',
    size = 'default',
    align = 'left',
    display = 'default',
    isLoading = false,
    isDisabled = false,
    isFullHeight = false,
    isFullWidth = false,
    onClick = () => {},
    icon = null,
    iconAtTop = false,
    ...rest
  },
  ref
) {
  return (
    <StyledButton
      ref={ref}
      type={type}
      size={size}
      align={align}
      icon={icon}
      iconAtTop={iconAtTop}
      display={display}
      variant={variant}
      isFullHeight={isFullHeight}
      isFullWidth={isFullWidth}
      disabled={isDisabled}
      onClick={onClick}
      {...rest}>
      {renderBody({
        display,
        variant,
        align,
        isLoading,
        icon,
        iconAtTop,
        size,
        isFullWidth,
        children
      })}
    </StyledButton>
  )
})

Button.propTypes = {
  /** Callback when the button is clicked. */
  onClick: PropTypes.func,
  /** Whether the button is in a loading state. */
  isLoading: PropTypes.bool,
  /** Whether the button is disalbed or not. */
  isDisabled: PropTypes.bool,
  /** Whether the button takes up the full height of its container. */
  isFullHeight: PropTypes.bool,
  /** Whether the button takes up the full width of its container. */
  isFullWidth: PropTypes.bool,
  /** The default behavior of the button. */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Determines the size of the button. */
  size: PropTypes.oneOf(['small', 'medium', 'default', 'large']),
  /** Sets the text alignment. */
  align: PropTypes.oneOf(['left', 'right', 'center', 'center-left', 'center-right']),
  /** Sets the background color of the button. Overrides `display` prop. */
  fill: PropTypes.string,
  /** Sets the text color of the button. Overrides the `display` prop. */
  color: PropTypes.string,
  /** Sets the predefined styling applied to the button. */
  display: PropTypes.oneOf(['default', 'promo', 'monochrome', 'blue', 'orange', 'red']),
  /** Which style variant to use. */
  variant: PropTypes.oneOf(['outlined', 'outlined-cta', 'cta', 'text', 'link']),
  /** Whether the icon is atop the text */
  iconAtTop: PropTypes.bool,
  /** Displays an icon inside the button. */
  icon: PropTypes.elementType
}

export { Button, styles, paddings, fontSizes, fontColors }
